import Link from 'next/link';
import snakeCase from 'lodash/snakeCase';
import styled from 'styled-components';

import { IFooterBreadcrumb, IFooterData } from 'layout/footer/types/FooterData';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { UnorderedList } from 'shared/components/UnorderedList';
import { variableFontMedium, variableFontRegular } from 'styles/theme';

const sortFooterLinks = (data) => {
  const desiredOrder = ['About', 'Support', 'Buy', 'Sell'];

  return data.sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.title);
    const indexB = desiredOrder.indexOf(b.title);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

export const FooterNavigationMobile = ({ footerLinks }: { footerLinks: Array<IFooterData> }) => {
  const sortedFooterLinks = sortFooterLinks(footerLinks);

  const handleBreadcrumbClick = (name: string, activeLink: string, onClick?: () => void) => {
    sendTrackingEvent('NAVIGATION_FOOTER_DRILLDOWN_TAP', {
      footer_title: snakeCase(activeLink),
      footer_subcategory: snakeCase(name),
    });

    onClick?.();
  };

  return (
    <FooterNavigationMobile.Wrapper>
      {sortedFooterLinks.map((footerLink) => (
        <section key={footerLink.id}>
          <h6 data-qa={`footer_${snakeCase(footerLink.title?.toLowerCase())}`}>
            {footerLink.title}
          </h6>
          <UnorderedList>
            {footerLink.breadcrumbs.map((breadcrumb: IFooterBreadcrumb) => {
              const isLink = !breadcrumb.onClick;

              if (isLink) {
                return (
                  <li key={breadcrumb.id}>
                    <Link
                      href={breadcrumb.externalUrl || `/${breadcrumb.slug}`}
                      passHref
                      data-qa={`footer_nav_${snakeCase(breadcrumb.name)}`}
                      onClick={() => handleBreadcrumbClick(breadcrumb.name, footerLink.title)}
                      onKeyDown={() => handleBreadcrumbClick(breadcrumb.name, footerLink.title)}
                      role="link"
                      tabIndex={0}
                      target={breadcrumb.openInNewTab ? '_blank' : '_self'}
                      rel="noreferrer noopener"
                    >
                      {breadcrumb.name}
                    </Link>
                  </li>
                );
              }

              if (breadcrumb.onClick) {
                return (
                  <li key={breadcrumb.id}>
                    <div
                      data-qa={`footer_nav_${snakeCase(breadcrumb.name)}`}
                      className="footerNavLinkCta"
                      onClick={() =>
                        handleBreadcrumbClick(breadcrumb.name, footerLink.title, breadcrumb.onClick)
                      }
                      onKeyDown={() =>
                        handleBreadcrumbClick(breadcrumb.name, footerLink.title, breadcrumb.onClick)
                      }
                      role="button"
                      tabIndex={0}
                    >
                      {breadcrumb.name}
                    </div>
                  </li>
                );
              }

              return <li key={breadcrumb.id}>{breadcrumb.name}</li>;
            })}
          </UnorderedList>
        </section>
      ))}
    </FooterNavigationMobile.Wrapper>
  );
};

FooterNavigationMobile.Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  h6 {
    ${variableFontMedium({ size: '12px', height: '12px' })};
    margin: 16px;
  }

  li {
    ${variableFontRegular({ size: '11px', height: '11px' })}
    margin: 16px;

    @media (hover: hover) {
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .footerNavLinkCta {
    cursor: pointer;
  }
`;
